import { z } from "zod";

import { cnpjSchema, cpfSchema } from "./br-tax-id";
import { brazilPhoneSchema } from "./phone";

export const BrazilPixKeyType = z.enum(["cpf", "cnpj", "email", "phone", "random"]);

export type BrazilPixKeyType = z.infer<typeof BrazilPixKeyType>;

const accountHolderName = z.string().trim().max(120).optional().or(z.literal(""));

const optionalBrazilTaxIdSchema = z
  .union([cpfSchema, cnpjSchema])
  .optional()
  .describe("Optional CPF or CNPJ used by the account holder.");

const pixKeyValidators: Record<BrazilPixKeyType, z.ZodType> = {
  cpf: cpfSchema,
  cnpj: cnpjSchema,
  email: z.email(),
  phone: brazilPhoneSchema,
  random: z.uuid(),
};

const BrazilPixBankingInfoSchema = z
  .object({
    method: z.literal("pix"),
    name: accountHolderName,
    taxId: optionalBrazilTaxIdSchema,
    pixKeyType: BrazilPixKeyType.optional(),
    pixKey: z.string().trim().max(140).optional().or(z.literal("")),
  })
  .refine(
    ({ pixKeyType, pixKey }) =>
      !pixKeyType || !pixKey || pixKeyValidators[pixKeyType].safeParse(pixKey).success,
    { message: "clients_validation_pix_key_type", path: ["pixKey"] },
  )
  .describe("Brazil PIX payment details.");

const BrazilBankAccountInfoSchema = z
  .object({
    method: z.literal("bank"),
    name: accountHolderName,
    taxId: optionalBrazilTaxIdSchema,
    bankCode: z
      .string()
      .trim()
      .max(10)
      .regex(/^\d+$/, {
        message: "clients_validation_bank_code_digits",
      })
      .optional()
      .or(z.literal("")),
    agency: z.string().trim().max(20).optional().or(z.literal("")),
    account: z.string().trim().max(30).optional().or(z.literal("")),
  })
  .describe("Brazil bank account transfer details.");

export const BrazilBankingInfoSchema = z
  .discriminatedUnion("method", [BrazilPixBankingInfoSchema, BrazilBankAccountInfoSchema])
  .describe("Brazil banking details for PIX or bank transfer.");

export type BrazilBankingInfo = z.infer<typeof BrazilBankingInfoSchema>;
